import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePost } from '../API';
import InterfaceContext from '../Context';
import { useLocalizedStrings } from '../Localization';
import { languageContent, safeParse } from '../Util';
import { QuestionProps } from './Props';
import { smartyDpvMatchCodeToString } from '@aidkitorg/types/lib/address/geocode';
import { Alert, Icon, Button as AKButton, useToast } from "@aidkitorg/component-library";
import { extractSecondaryFromStreet } from "@aidkitorg/types/lib/util";

import { useModularMarkdown } from '../Hooks/ModularMarkdown';

const states = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AS", name: "American Samoa" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" }, { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "GU", name: "Guam" },
  { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
  { code: "MP", name: "Northern Mariana Islands" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" },
  { code: "PW", name: "Palau" }, { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "DC", name: "Washington DC" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
  { code: "VI", name: "U.S. Virgin Islands" }, { code: "FM", name: "Federated States of Micronesia" },
  { code: "MH", name: "Marshall Islands" }, { code: "AA", name: "Armed Forces Americas" },
  { code: "AE", name: "Armed Forces Europe" }, { code: "AP", name: "Armed Forces Pacific" }
];

export function AddressQuestion(props: QuestionProps): JSX.Element {
  const rawMetadata = props["Metadata"] || '{}';
  const metadata = useMemo(() => safeParse(rawMetadata), [rawMetadata]);

  const L = useLocalizedStrings();
  const { toast } = useToast();
  const [street, setStreet] = useState<string>(props.info[props['Target Field']! + '_street'] || '');
  const [city, setCity] = useState<string>(props.info[props['Target Field']! + '_city'] || '');
  const [state, setState] = useState<string>(props.info[props['Target Field']! + '_state'] || '');
  const [zip, setZip] = useState<string>(props.info[props['Target Field']! + '_zip'] || '');
  const [secondary, setSecondary] = useState<string>('');
  const [confirmedStreetWithUnmatchedSecondary, setConfirmedStreetWithUnmatchedSecondary] = useState<string>('');

  const [proposedStreet, setProposedStreet] = useState<string>('');
  const [proposedCity, setProposedCity] = useState<string>('');
  const [proposedState, setProposedState] = useState<string>('');
  const [proposedZip, setProposedZip] = useState<string>('');
  const [proposedPlus4, setProposedPlus4] = useState<string>('');
  const [proposedLatLng, setProposedLatLng] = useState<number[]>([]);
  const [proposedDPB, setProposedDPB] = useState<string>('');
  const [proposedCounty, setProposedCounty] = useState<string>('');
  const [proposedTract, setProposedTract] = useState<string>('');
  const [proposedBuilding, setProposedBuilding] = useState<string>('');
  const [proposedSubpremise, setProposedSubpremise] = useState<string>('');
  const [proposedLookups, setProposedLookups] = useState<Record<string, string>>({});
  const [dpvMatch, setDPVMatch] = useState<string>('');
  const [dirty, setDirty] = useState<boolean>(false);
  const [matched, setMatched] = useState<null | 'yes' | 'no'>(null);
  const [streetSuggestion, setStreetSuggestion] = useState<string>('');

  const streetInput = useRef<HTMLInputElement>(null);

  let snapshot = useRef<{
    street: string;
    city: string;
    state: string;
    zip: string;
    secondary: string;
  } | undefined>(undefined);

  useEffect(() => {
    // If we haven't loaded info for the applicant yet, don't eagerly
    // clear out the info keys. Wait until we're able to fetch applicant info.
    if (!props.info || Object.keys(props.info).length === 0) { return; }

    snapshot.current = {
      street: street,
      city: city,
      state: state,
      zip: zip,
      secondary: secondary
    };
    if (street === '' && city === '' && state === '' && zip === '') {
      props.setInfoKey(props['Target Field']!, '', true, false);
      props.setInfoKey(props['Target Field']! + '_street', '', true, false);
      props.setInfoKey(props['Target Field']! + '_city', '', true, false);
      props.setInfoKey(props['Target Field']! + '_state', '', true, false);
      props.setInfoKey(props['Target Field']! + '_zip', '', true, false);
      props.setInfoKey(props['Target Field']! + '_dpb', '', true, false);
      props.setInfoKey(props['Target Field']! + '_lat', '', true, false);
      props.setInfoKey(props['Target Field']! + '_lng', '', true, false);
      props.setInfoKey(props['Target Field']! + '_county', '', true, false);
      props.setInfoKey(props['Target Field']! + '_tract', '', true, false);
      props.setInfoKey(props['Target Field']! + '_building', '', true, false);
      props.setInfoKey(props['Target Field']! + '_subpremise', '', true, false);
      for (const lookup of metadata.lookups || []) {
        props.setInfoKey(props['Target Field']! + '_' + lookup, '', true, false);
      }
    }
  }, [street, city, state, zip, secondary]);

  useEffect(() => {
    if (!dirty) {
      const savedStreet = props.info[props['Target Field']! + '_street'] || '';
      const savedSubpremise = props.info[props['Target Field']! + '_subpremise'] || '';
      // Display the complete address with apartment in the UI
      const displayStreet = savedStreet + (savedSubpremise ? ' #' + savedSubpremise : '');
      setStreet(displayStreet);
      setCity(props.info[props['Target Field']! + '_city'] || '');
      setState(props.info[props['Target Field']! + '_state'] || '');
      setZip(props.info[props['Target Field']! + '_zip'] || '');
    }
  }, [props.info]);

  useEffect(() => {
    // debounced listener to street field to check for common typos and offer suggestions
    const debouncedTimeout = setTimeout(
      function checkForTypos(){
        if (street) {
          let suggestion = '';
          // fix common typo (eg. "123Main St" => "123 Main St")
          let streetError = street.match(/\b\d+[a-zA-Z]{3}/g);
          if (streetError){
            for(const s of streetError){
              suggestion = street.replaceAll(s, [s.slice(0, s.length-3), s.slice(-3)].join(' '));
            }
          }
          // fix common typo (eg. "123 Main St#456" => "123 Main St #456")
          streetError = (suggestion || street).match(/\S#/g);
          if (streetError){
            for(const s of streetError){
              suggestion = (suggestion || street).replaceAll(s, [s.slice(0, s.length-1), s.slice(-1)].join(' '));
            }
          }
          setStreetSuggestion(suggestion);
        }
      },
      100
    );
    return () => clearTimeout(debouncedTimeout);
  }, [street])

  const geocode = usePost('/geo/address');
  // For debouncing the address form
  const [ timer, setTimer ] = useState<ReturnType<typeof setTimeout>>();

  function queueGeocoding(params: {street: string, city: string, state: string, zip: string}) {
    setDirty(true);
    setMatched(null);
    if (params.street.trim() && params.city.trim() && params.state.trim()) {
      const newTimer = (setTimeout(async () => {
        const streetToUse = (confirmedStreetWithUnmatchedSecondary && snapshot.current?.secondary) ? confirmedStreetWithUnmatchedSecondary : snapshot.current!.street;
        const extracted = extractSecondaryFromStreet(streetToUse);
        const finalStreet = extracted.street || streetToUse;
        const finalSecondary = snapshot.current!.secondary || extracted.secondary;
        
        const backendResponse = await geocode({
          addressComponents: {
            street: finalStreet,
            secondary: finalSecondary,
            city: snapshot.current!.city,
            state: snapshot.current!.state,
            zipcode: snapshot.current!.zip
          },
          geocoder: 'smarty',
          lookups: metadata.lookups || []
        });
        
        // Update the street display with the extracted secondary if found
        setStreet(finalStreet + (finalSecondary ? ' #' + finalSecondary : ''));
        if (finalSecondary && !snapshot.current!.secondary) {
          setSecondary(finalSecondary);
        }

        setDPVMatch(backendResponse?.smartyResponse?.analysis?.dpv_match_code || '');

        const extractedSmartySecondary = extractSecondaryFromStreet((backendResponse?.smartyResponse?.components.street || '') + ' ' + (backendResponse?.smartyResponse?.components.secondary || ''));
        if (backendResponse?.smartyResponse?.analysis.dpv_match_code === 'Y' || (backendResponse?.smartyResponse?.analysis.dpv_match_code === 'S' && extractedSmartySecondary.secondary.trim() === finalSecondary.trim())) {
          setMatched('yes');
          setDPVMatch('Y');

          setProposedStreet(backendResponse.smartyResponse.components.street + (backendResponse.smartyResponse.components.secondary ? ', ' + backendResponse.smartyResponse.components.secondary : ''));
          setProposedCity(backendResponse.smartyResponse.components.city_name);
          setProposedState(backendResponse.smartyResponse.components.state_abbreviation);
          setProposedZip(backendResponse.smartyResponse.components.zipcode);
          setProposedPlus4(backendResponse.smartyResponse.components.plus4_code);
          setProposedDPB(backendResponse.smartyResponse.delivery_point_barcode);
          setProposedCounty(backendResponse.county || '');
          setProposedTract(backendResponse.census_tract || '');
          setProposedSubpremise(backendResponse.subpremise || '');
          setProposedBuilding(backendResponse.building || '');
          setProposedLatLng([backendResponse.smartyResponse.metadata.latitude, backendResponse.smartyResponse.metadata.longitude])
          setProposedLookups(backendResponse.lookups || {});

          if (backendResponse.smartyResponse.analysis.dpv_match_code === 'Y') {
            setSecondary('');
          }
        } else if (backendResponse?.smartyResponse?.analysis.dpv_match_code === 'S' || backendResponse?.smartyResponse?.analysis.dpv_match_code === 'D') {
          // Temporarily hold onto the street address that Smarty has confirmed, removing the invalid secondary.
          // If the applicant changes the secondary, we'll re-geocode with the new secondary and this valid street address.
          // If the applicant asserts that the address was right the first time, we disregard this variable.
          setConfirmedStreetWithUnmatchedSecondary(backendResponse.smartyResponse.components.street)
          setMatched('yes');
          if (secondary) {
            toast({
              description: (L.questions.identity.no_address_with_given_secondary_found).replace('$secondary', secondary),
              variant: 'error',
              duration: 8000
            });
          }
          setSecondary('')
        } else if (backendResponse?.googleMapsResponse) {
          setMatched('yes');
          // Consider a google maps match as DPV match so our UI responds accordingly. This will require a larger refactor effort 
          // one day to have streamlined logic between both providers.
          setDPVMatch('Y');
          setProposedStreet(`${backendResponse.googleMapsResponse.street_number} ${backendResponse.googleMapsResponse.street_name}` + (backendResponse.googleMapsResponse.subpremise ? ` ${backendResponse.googleMapsResponse.subpremise}` : ''))
          setProposedSubpremise(backendResponse.googleMapsResponse.subpremise);
          setProposedCity(backendResponse.googleMapsResponse.city);
          setProposedState(backendResponse.googleMapsResponse.state);
          setProposedZip(backendResponse.googleMapsResponse.zipcode);
          setProposedPlus4(backendResponse.googleMapsResponse.plus4_code);
          setProposedCounty(backendResponse.googleMapsResponse.county);
          setProposedLatLng([backendResponse.googleMapsResponse.lat, backendResponse.googleMapsResponse.lng]);
          setProposedLookups(backendResponse.lookups || {});
        } else { // If DPV match code is N, null, or undefined
          setMatched('no');
        }
      }, 700));
      clearTimeout(timer);
      setTimer(newTimer);
    }
  }

  function confirmProposal() {
    // Extract apartment from proposedStreet
    const extracted = extractSecondaryFromStreet(proposedStreet);
    const finalStreet = extracted.secondary ? extracted.street : proposedStreet;
    const finalSubpremise = proposedSubpremise || extracted.secondary;
    const displayStreet = finalStreet + (finalSubpremise ? ' #' + finalSubpremise : '');
    // Include apartment as separate comma-delimited field for proper parsing
    const fullAddress = finalSubpremise 
      ? `${finalStreet}, #${finalSubpremise}, ${proposedCity}, ${proposedState} ${proposedZip}`
      : `${finalStreet}, ${proposedCity}, ${proposedState} ${proposedZip}`;
    
    props.setInfoKey(props['Target Field']!, fullAddress, true, false);
    props.setInfoKey(props['Target Field']! + '_street', finalStreet, true, false);
    props.setInfoKey(props['Target Field']! + '_city', proposedCity, true, false);
    props.setInfoKey(props['Target Field']! + '_state', proposedState, true, false);
    props.setInfoKey(props['Target Field']! + '_zip', proposedZip, true, false);
    props.setInfoKey(props['Target Field']! + '_plus_4', proposedPlus4, true, false);
    props.setInfoKey(props['Target Field']! + '_dpb', proposedDPB, true, false);
    props.setInfoKey(props['Target Field']! + '_county', proposedCounty, true, false);
    props.setInfoKey(props['Target Field']! + '_tract', proposedTract, true, false);
    props.setInfoKey(props['Target Field']! + '_subpremise', finalSubpremise, true, false);
    props.setInfoKey(props['Target Field']! + '_building', proposedBuilding, true, false);
    props.setInfoKey(props['Target Field']! + '_lat', proposedLatLng.length ? proposedLatLng[0].toString() : '', true, false);
    props.setInfoKey(props['Target Field']! + '_lng', proposedLatLng.length ? proposedLatLng[1].toString() : '', true, false);
    props.setInfoKey(props['Target Field']! + '_validation', smartyDpvMatchCodeToString(dpvMatch), true, false);
    for (const lookup of metadata.lookups || []) {
      props.setInfoKey(props['Target Field']! + '_' + lookup, proposedLookups[lookup] || '', true, false);
    }
    
    // Update UI immediately to display the complete address with apartment
    setStreet(displayStreet);
    setCity(proposedCity);
    setState(proposedState);
    setZip(proposedZip);
    
    // Clear proposal state
    setConfirmedStreetWithUnmatchedSecondary('');
    setSecondary('');
    setProposedStreet('');
    setProposedCity('');
    setProposedState('');
    setProposedDPB('');
    setProposedCounty('');
    setProposedTract('');
    setProposedSubpremise('');
    setProposedBuilding('');
    setProposedZip('');
    setProposedPlus4('');
    setProposedLatLng([]);
    setProposedLookups({});
    setDirty(false);
    setMatched(null);
  }

  function clearProposal() {
    setProposedStreet('');
    setProposedCity('');
    setProposedState('');
    setProposedDPB('');
    setProposedCounty('');
    setProposedTract('');
    setProposedSubpremise('');
    setProposedBuilding('');
    setProposedZip('');
    setProposedPlus4('');
    setSecondary('');
    setConfirmedStreetWithUnmatchedSecondary('');
    setProposedLatLng([]);
    setProposedLookups({});
    setDPVMatch('');
    setMatched(null);
    setDirty(false);

    setStreet('');
    streetInput.current?.focus();
  }

  function confirmAddressThatWasNotFound() {
    const extracted = extractSecondaryFromStreet(street);
    const finalStreet = extracted.street || street.trim();
    const finalSecondary = secondary || extracted.secondary;
    
    const sanitizedStreet = finalStreet.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    const sanitizedCity = city.trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    const sanitizedState = state.trim().toUpperCase();
    const sanitizedZip = zip.toString().trim().slice(0, 5).replace(/\D/g, "");
    const sanitizedSecondary = finalSecondary.trim();

    // Include apartment number in the combined address field if present
    const fullAddress = sanitizedSecondary 
      ? `${sanitizedStreet}, #${sanitizedSecondary}, ${sanitizedCity}, ${sanitizedState} ${sanitizedZip}`
      : `${sanitizedStreet}, ${sanitizedCity}, ${sanitizedState} ${sanitizedZip}`;

    props.setInfoKey(props['Target Field']!, fullAddress, true, false);
    props.setInfoKey(props['Target Field']! + '_street', sanitizedStreet, true, false);
    props.setInfoKey(props['Target Field']! + '_city', sanitizedCity, true, false);
    props.setInfoKey(props['Target Field']! + '_state', sanitizedState, true, false);
    props.setInfoKey(props['Target Field']! + '_zip', sanitizedZip, true, false);
    props.setInfoKey(props['Target Field']! + '_subpremise', sanitizedSecondary, true, false);
    props.setInfoKey(props['Target Field']! + '_county', '', true, false);
    props.setInfoKey(props['Target Field']! + '_lat', '', true, false);
    props.setInfoKey(props['Target Field']! + '_lng', '', true, false);
    props.setInfoKey(props['Target Field']! + '_validation', smartyDpvMatchCodeToString(dpvMatch), true, false);
    for (const lookup of metadata.lookups || []) {
      props.setInfoKey(props['Target Field']! + '_' + lookup, '', true, false);
    }
    setConfirmedStreetWithUnmatchedSecondary('');
    setProposedStreet('');
    setProposedCity('');
    setProposedState('');
    setProposedDPB('');
    setProposedCounty('');
    setProposedTract('');
    setProposedSubpremise('');
    setProposedBuilding('');
    setProposedZip('');
    setProposedPlus4('');
    setProposedLatLng([]);
    setDirty(false);
    setMatched(null);
  }

  const allowApplicantToAssertValidAddress = (props["Additional Options"] || []).indexOf("Force Geocoding") === -1;

  const context = useContext(InterfaceContext);
  const content = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info
  });

  return <div>
    <legend>
      {content}
    </legend>
    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
      <div className="sm:col-span-6">
        <label htmlFor="street-address" className="block text-sm font-medium text-gray-700">
          {L.questions.identity.street_address}
        </label>
        <div className="mt-1">
          <input
            value={street}
            ref={streetInput}
            onChange={e => {
              const streetInput = e.target.value;
              setStreet(streetInput);
              queueGeocoding({street: streetInput, city, state, zip});
            }}
            type="text"
            name="street-address"
            id="street-address"
            autoComplete="street-address"
            className="shadow-sm p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500"
          />
          { streetSuggestion && <div className="-mx-2"><Alert
            dismissible={true}
            variant="warning">
            <div className="flex flex-col gap-2">
              <p className="text-sm mb-0">{L.questions.textentry.use_this_address}</p>
              <AKButton 
                variant="secondary"
                icon={<Icon name="CheckCircleIcon" />} 
                onClick={() => { setStreet(streetSuggestion); setStreetSuggestion(''); }}>{streetSuggestion}</AKButton>
            </div>
          </Alert></div>}
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="city" className="block text-sm font-medium text-gray-700">
          {L.questions.identity.city}
        </label>
        <div className="mt-1">
          <input
            value={city}
            onChange={e => {
              setCity(e.target.value);
              queueGeocoding({ street, city: e.target.value, state, zip });
            }}
            type="text"
            name="city"
            id="city"
            autoComplete="address-level2"
            className="shadow-sm p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="state" className="block text-sm font-medium text-gray-700">
          {L.questions.identity.state}
        </label>
        <div className="mt-1">
          {/* Backwards-compatibility for state values in the DB that aren't two-letter state abbreviations */}
          {(states.some(s => s.code === state) || !state) ? (
            <select
              id="state"
              name="state"
              autoComplete="address-level1"
              value={state}
              onChange={e => {
                setState(e.target.value);
                queueGeocoding({ street, city, state: e.target.value, zip });
              }}
              className="shadow-sm p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500"
            >
              <option value="" disabled></option>
              {states.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name} ({code})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              id="state"
              name="state"
              value={state}
              onChange={e => {
                setState(e.target.value);
                queueGeocoding({ street, city, state: e.target.value, zip });
              }}
              className="shadow-sm p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500"
            />
          )}
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="postal-code" className="block text-sm font-medium text-gray-700">
          {L.questions.identity.zip_code}
        </label>
        <div className="mt-1">
          <input
            value={zip}
            onChange={e => {
              setZip(e.target.value);
              queueGeocoding({ street, city, state, zip: e.target.value });
            }}
            type="text"
            name="postal-code"
            id="postal-code"
            autoComplete="postal-code"
            className="shadow-sm p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500"
          />
        </div>
      </div>

      {matched === 'no' && <div className="sm:col-span-6">
        <div>
          <div className="block text-sm font-medium text-gray-700">
            <span>{L.questions.identity.address_not_found + (allowApplicantToAssertValidAddress ? ' ' + L.questions.identity.address_not_found_allow_force : '')}</span>
          </div>
          {allowApplicantToAssertValidAddress &&
          <button
            className="inline-flex items-center mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={confirmAddressThatWasNotFound}>
            {L.questions.identity.address_force_assertion}
          </button>
          }
        </div>
      </div>}
      {(matched === 'yes' || (proposedStreet && matched !== 'no')) &&
        <div className="sm:col-span-6">
          <div className="block text-sm font-medium text-gray-700">
            {dpvMatch === 'Y' && <>
              <div className='font-bold'>{L.questions.identity.is_this_the_right_address}</div>
              <div>{proposedStreet}</div>
              <div>{proposedCity}, {proposedState} {proposedZip}</div>
              <button
                className="inline-flex items-center mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={confirmProposal}>
                {L.questions.choice.yes}
              </button>
              <button
                className="inline-flex items-center mt-2 ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => { allowApplicantToAssertValidAddress ? confirmAddressThatWasNotFound() : clearProposal()}}>
                {L.questions.choice.no}
              </button>
            </>}
            {dpvMatch !== 'Y' && <div>
              <div className='font-bold text-red-500'>
                {L.questions.identity.we_arent_sure_this_address_is_correct}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="unit" className="block text-sm font-medium text-gray-700">
                  {L.questions.identity.what_is_your_apartment_number}
                </label>
                <div className="mt-1">
                  <input
                    value={secondary}
                    onChange={e => {
                      setSecondary(e.target.value);
                    }}
                    type="text"
                    name="unit"
                    id="unit"
                    className="shadow-sm p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring focus:ring-indigo-500"
                  />
                </div>
                <button
                  className="inline-flex items-center mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => queueGeocoding({street, city, state, zip})}>
                  {L.questions.identity.check_unit_number}
                </button>
                {allowApplicantToAssertValidAddress &&
                  <div className="pt-3">
                    {L.questions.identity.address_not_found_allow_force}
                    <button
                      className="inline-flex items-center mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={confirmAddressThatWasNotFound}>
                      {L.questions.identity.address_force_assertion}
                    </button>
                  </div>
                }
              </div>
            </div>}
          </div>
        </div>}
    </div>
  </div>;
}
